import React, { useState } from "react";
import { Link } from "react-router-dom";
import * as stock from "../../../services/inventoryStockService";
import { DataTable, act, hint, linkBtn } from "../../Fin/gl/glUi";
import { EmptyState, LoadState, PageHeader, PrimaryButton, SecondaryButton, SlidePanel, Tabs, formatDate, inputStyle, pageStyle, useLoad } from "../../../components/ui";
import { ItemPicker, TrackingInput, WarehouseSelect, cleanDetails, fmtAmt, fmtQty, useInvSettings } from "./invUi";

// Inventory → Stock: what is where (by warehouse, with bins and min / max), batches close to expiry,
// batch age (tyre DOT dates), where a serial number has been, exchange deposits waiting for the old
// unit, and the stock value (items by warehouse and class, plus the Dealership's vehicles).

const MOVE_TYPES = [
  { value: "PURCHASE_IN", label: "Stock in (purchase)" },
  { value: "ADJUSTMENT_IN", label: "Adjustment (increase)" },
  { value: "ADJUSTMENT_OUT", label: "Adjustment (decrease)" },
  { value: "WASTAGE", label: "Wastage / damage" },
];

// A stock move on one item in one warehouse, with its batches or serials.
export function StockMovePanel({ onHide, onDone, item: preset = null }) {
  const [item, setItem] = useState(preset);
  const [h, setH] = useState({ txn_type: "ADJUSTMENT_IN", qty: "", unit_cost: "", warehouse_id: null, reason: "" });
  const [details, setDetails] = useState([]);
  const [busy, setBusy] = useState(false);
  const out = ["ADJUSTMENT_OUT", "WASTAGE"].includes(h.txn_type);
  const save = async () => {
    setBusy(true);
    const res = await act(() => stock.postStockMove({ item_id: item && item.id, txn_type: h.txn_type, qty_change: Number(h.qty), unit_cost: h.txn_type === "PURCHASE_IN" ? Number(h.unit_cost) : undefined, warehouse_id: h.warehouse_id, reason: h.reason, details: cleanDetails(details) }));
    setBusy(false);
    if (res) {
      onDone && onDone();
      onHide();
    }
  };
  return (
    <SlidePanel show onHide={() => !busy && onHide()} title="Stock in / adjust" width={640} footer={<><SecondaryButton onClick={onHide} disabled={busy}>Cancel</SecondaryButton><PrimaryButton onClick={save} disabled={busy || !item}>Post</PrimaryButton></>}>
      <div className="mb-2">
        <label className="form-label" htmlFor="sm-wh">Warehouse</label>
        <WarehouseSelect id="sm-wh" value={h.warehouse_id} onChange={(v) => setH({ ...h, warehouse_id: v })} placeholder="Default warehouse" />
      </div>
      <div className="mb-2">
        <label className="form-label" htmlFor="sm-item">Item</label>
        {preset ? <div><b>{preset.sku}</b> {preset.name}</div> : <ItemPicker id="sm-item" value={item ? item.id : null} warehouseId={h.warehouse_id} onChange={setItem} />}
      </div>
      <div className="d-flex flex-wrap gap-2 mb-2">
        <select className="form-select" style={{ width: 220 }} aria-label="Type" value={h.txn_type} onChange={(e) => setH({ ...h, txn_type: e.target.value })}>
          {MOVE_TYPES.map((t) => (
            <option key={t.value} value={t.value}>
              {t.label}
            </option>
          ))}
        </select>
        <input type="number" step="any" min={0} className="form-control" style={{ ...inputStyle, width: 120 }} placeholder="Quantity" aria-label="Quantity" value={h.qty} onChange={(e) => setH({ ...h, qty: e.target.value })} />
        {h.txn_type === "PURCHASE_IN" ? <input type="number" step="any" min={0} className="form-control" style={{ ...inputStyle, width: 140 }} placeholder="Unit cost" aria-label="Unit cost" value={h.unit_cost} onChange={(e) => setH({ ...h, unit_cost: e.target.value })} /> : null}
      </div>
      <TrackingInput item={item} qty={h.qty} value={details} onChange={setDetails} mode={out ? "out" : "in"} itemId={item && item.id} warehouseId={h.warehouse_id} />
      <input className="form-control mt-2" style={inputStyle} placeholder="Reason" aria-label="Reason" value={h.reason} onChange={(e) => setH({ ...h, reason: e.target.value })} />
      <div style={{ ...hint, marginTop: 6 }}>Goods from a supplier are better received on a goods receipt (Purchasing), so the bill can be matched to it.</div>
    </SlidePanel>
  );
}

function ByWarehouse({ s }) {
  const [wh, setWh] = useState(null);
  const [q, setQ] = useState("");
  const [moving, setMoving] = useState(false);
  const { data, loading, error, reload } = useLoad(() => stock.getBalances({ warehouse_id: wh || undefined, q: q || undefined, size: 300 }), [wh, q]);
  return (
    <>
      <div className="d-flex flex-wrap gap-2 align-items-center mb-2">
        <div style={{ minWidth: 260 }}>
          <WarehouseSelect id="sw-wh" all value={wh} onChange={setWh} />
        </div>
        <input className="form-control" style={{ ...inputStyle, maxWidth: 280 }} placeholder="Search SKU or name" aria-label="Search" value={q} onChange={(e) => setQ(e.target.value)} />
        {s.can.manage ? <PrimaryButton onClick={() => setMoving(true)}>Stock in / adjust</PrimaryButton> : null}
      </div>
      <LoadState loading={loading} error={error} onRetry={reload}>
        <DataTable
          rows={data || []}
          rowKey={(r) => `${r.item_id}-${r.warehouse_id}`}
          empty={<EmptyState title="No stock" text="Stock appears here once it is received into a warehouse." />}
          columns={[
            { label: "Item", render: (r) => <Link to={`/inventory/${r.item_id}`}><b>{r.sku}</b> {r.item_name}</Link> },
            { label: "Warehouse", render: (r) => r.warehouse_code },
            { label: "Bin", render: (r) => r.bin_location || "" },
            { label: "On hand", num: true, render: (r) => `${fmtQty(r.qty)} ${r.unit_of_measure || ""}` },
            { label: "Min / max", num: true, render: (r) => (r.min_qty !== null || r.max_qty !== null ? `${r.min_qty === null ? "–" : fmtQty(r.min_qty)} / ${r.max_qty === null ? "–" : fmtQty(r.max_qty)}` : "") },
            { label: "", render: (r) => (r.min_qty !== null && Number(r.qty) < Number(r.min_qty) ? <span style={{ color: "#B91C1C", fontSize: 12, fontWeight: 600 }}>Below min</span> : null) },
            { label: "Value", num: true, render: (r) => fmtAmt(Number(r.qty) * Number(r.purchase_cost)) },
          ]}
        />
      </LoadState>
      {moving ? <StockMovePanel onHide={() => setMoving(false)} onDone={reload} /> : null}
    </>
  );
}

function Expiring({ s }) {
  const [days, setDays] = useState(s.expiry_alert_days || 30);
  const { data, loading, error, reload } = useLoad(() => stock.getExpiring({ days }), [days]);
  return (
    <>
      <label className="d-flex align-items-center gap-2 mb-2" style={{ fontSize: 14 }}>
        Expiring within
        <input type="number" min={0} className="form-control" style={{ width: 100 }} value={days} onChange={(e) => setDays(e.target.value)} aria-label="Days" />
        days
      </label>
      <LoadState loading={loading} error={error} onRetry={reload}>
        <DataTable
          rows={data || []}
          empty={<EmptyState title="Nothing expiring" />}
          columns={[
            { label: "Item", render: (r) => `${r.sku} ${r.item_name}` },
            { label: "Batch", render: (r) => r.lot_no },
            { label: "Warehouse", render: (r) => r.warehouse_code },
            { label: "Expiry", render: (r) => formatDate(r.expiry_date) },
            { label: "Days left", num: true, render: (r) => <span style={{ color: Number(r.days_left) < 0 ? "#B91C1C" : undefined, fontWeight: 600 }}>{Number(r.days_left) < 0 ? "Expired" : r.days_left}</span> },
            { label: "Qty", num: true, render: (r) => fmtQty(r.qty) },
            { label: "Value", num: true, render: (r) => fmtAmt(Number(r.qty) * Number(r.purchase_cost)) },
          ]}
        />
      </LoadState>
    </>
  );
}

function Ages() {
  const { data, loading, error, reload } = useLoad(() => stock.getLotAges({}), []);
  return (
    <LoadState loading={loading} error={error} onRetry={reload}>
      <div style={{ ...hint, marginBottom: 8 }}>Batches with a manufacture date (tyres: the DOT week / year). Tyres are commonly not sold once older than 5 years from manufacture.</div>
      <DataTable
        rows={data || []}
        empty={<EmptyState title="No dated batches" />}
        columns={[
          { label: "Item", render: (r) => `${r.sku} ${r.item_name}` },
          { label: "Batch", render: (r) => r.lot_no },
          { label: "Warehouse", render: (r) => r.warehouse_code },
          { label: "Made", render: (r) => formatDate(r.mfg_date) },
          { label: "Age", num: true, render: (r) => <span style={{ color: r.age_months >= 60 ? "#B91C1C" : r.age_months >= 36 ? "#92400E" : undefined }}>{Math.floor(r.age_months / 12)}y {r.age_months % 12}m</span> },
          { label: "Qty", num: true, render: (r) => fmtQty(r.qty) },
        ]}
      />
    </LoadState>
  );
}

function SerialSearch() {
  const [sn, setSn] = useState("");
  const [rows, setRows] = useState(null);
  const find = async () => {
    try {
      const res = await stock.findSerial(sn.trim());
      setRows(res?.data || []);
    } catch (e) {
      setRows([]);
    }
  };
  return (
    <>
      <div className="d-flex gap-2 mb-2">
        <input className="form-control" style={{ ...inputStyle, maxWidth: 320 }} placeholder="Serial number" aria-label="Serial number" value={sn} onChange={(e) => setSn(e.target.value)} onKeyDown={(e) => e.key === "Enter" && find()} />
        <SecondaryButton onClick={find} disabled={!sn.trim()}>
          Find
        </SecondaryButton>
      </div>
      {rows === null ? null : rows.length ? (
        rows.map((r) => (
          <div key={r.id} className="mb-3">
            <div>
              <b>{r.serial_no}</b> · {r.sku} {r.item_name} · {r.status.toLowerCase().replace("_", " ")}
              {r.warehouse_code ? ` in ${r.warehouse_code}` : ""}
              {r.issued_ref ? ` · issued on ${r.issued_ref}` : ""}
            </div>
            <DataTable rows={r.moves} columns={[{ label: "When", render: (m) => formatDate(m.created_at) }, { label: "Move", render: (m) => m.txn_type }, { label: "Warehouse", render: (m) => m.warehouse_code || "" }, { label: "Reference", render: (m) => m.reason || "" }]} />
          </div>
        ))
      ) : (
        <EmptyState title="Serial number not found" />
      )}
    </>
  );
}

function Deposits({ s }) {
  const [status, setStatus] = useState("OPEN");
  const [returning, setReturning] = useState(null);
  const [wh, setWh] = useState(null);
  const { data, loading, error, reload } = useLoad(() => stock.listCoreDeposits({ status }), [status]);
  const back = async () => {
    if (await act(() => stock.coreDepositAction(returning.id, "returned", { warehouse_id: wh }))) {
      setReturning(null);
      reload();
    }
  };
  return (
    <>
      <select className="form-select mb-2" style={{ width: 200 }} aria-label="Status" value={status} onChange={(e) => setStatus(e.target.value)}>
        <option value="OPEN">Waiting for the old unit</option>
        <option value="RETURNED">Returned</option>
        <option value="FORFEITED">Kept (not returned)</option>
        <option value="ALL">All</option>
      </select>
      <LoadState loading={loading} error={error} onRetry={reload}>
        <DataTable
          rows={data || []}
          empty={<EmptyState title="No exchange deposits" text="Items with an exchange charge (batteries, alternators ...) take a deposit when sold." />}
          columns={[
            { label: "Sold on", render: (r) => r.source_doc_number || r.source_doc_type },
            { label: "Customer", render: (r) => r.customer_name || "" },
            { label: "Item", render: (r) => `${r.sku} ${r.item_name}` },
            { label: "Qty", num: true, render: (r) => fmtQty(r.quantity) },
            { label: "Deposit", num: true, render: (r) => fmtAmt(r.amount) },
            { label: "Date", render: (r) => formatDate(r.created_at) },
            {
              label: "",
              render: (r) =>
                r.status === "OPEN" ? (
                  <span className="d-flex gap-2">
                    {s.can.purchase ? <button type="button" style={linkBtn} onClick={() => setReturning(r)}>Old unit back</button> : null}
                    {s.can.manage ? <button type="button" style={linkBtn} onClick={() => act(() => stock.coreDepositAction(r.id, "forfeit")).then(reload)}>Keep deposit</button> : null}
                  </span>
                ) : (
                  r.status.toLowerCase()
                ),
            },
          ]}
        />
      </LoadState>
      {returning ? (
        <SlidePanel show onHide={() => setReturning(null)} title="Old unit returned" width={480} footer={<><SecondaryButton onClick={() => setReturning(null)}>Cancel</SecondaryButton><PrimaryButton onClick={back}>Refund the deposit</PrimaryButton></>}>
          <p style={{ fontSize: 14 }}>
            {fmtQty(returning.quantity)} × {returning.item_name} came back. The deposit of {fmtAmt(returning.amount)} is refunded on a credit memo{returning.core_sku ? `, and the old unit (${returning.core_sku}) goes into stock at no value` : ""}.
          </p>
          <label className="form-label" htmlFor="dep-wh">Into warehouse</label>
          <WarehouseSelect id="dep-wh" value={wh} onChange={setWh} placeholder="Default warehouse" />
        </SlidePanel>
      ) : null}
    </>
  );
}

function Value() {
  const { data, loading, error, reload } = useLoad(() => stock.getStockValue({}), []);
  return (
    <LoadState loading={loading} error={error} onRetry={reload}>
      {data ? (
        <>
          <DataTable
            rows={data.items}
            rowKey={(r) => `${r.warehouse_code}-${r.class_code}`}
            empty={<EmptyState title="No stock" />}
            columns={[
              { label: "Warehouse", render: (r) => r.warehouse_code },
              { label: "Class", render: (r) => r.class_name },
              { label: "Items", num: true, render: (r) => r.lines },
              { label: "Quantity", num: true, render: (r) => fmtQty(r.qty) },
              { label: "Value", num: true, render: (r) => fmtAmt(r.value) },
            ]}
          />
          {data.items_without_warehouse && data.items_without_warehouse.value ? <div style={hint}>Items outside any warehouse: {fmtAmt(data.items_without_warehouse.value)}</div> : null}
          {data.sections.map((sec) => (
            <div key={sec.key} className="mt-3">
              <h6>{sec.label}</h6>
              <DataTable rows={sec.rows} rowKey={(r) => `${sec.key}-${r.warehouse_code}-${r.reference}`} columns={[{ label: "Yard / warehouse", render: (r) => r.warehouse_code || "–" }, { label: "Group", render: (r) => r.group }, { label: "Reference", render: (r) => r.reference }, { label: "Description", render: (r) => r.description }, { label: "Value", num: true, render: (r) => fmtAmt(r.value) }]} />
            </div>
          ))}
          <div className="mt-3" style={{ fontSize: 16, fontWeight: 700, textAlign: "right" }}>Total stock value {fmtAmt(data.total)}</div>
          <div style={{ ...hint, textAlign: "right" }}>Consumables are expensed when received and are not in the value.</div>
        </>
      ) : null}
    </LoadState>
  );
}

export default function StockOverview() {
  const { data: s, loading, error, reload } = useInvSettings();
  const [tab, setTab] = useState("stock");
  const tabs = [
    { key: "stock", label: "By warehouse" },
    { key: "expiring", label: "Expiring" },
    { key: "ages", label: "Batch age" },
    { key: "serial", label: "Serial numbers" },
    { key: "deposits", label: "Exchange deposits" },
    { key: "value", label: "Stock value" },
  ];
  return (
    <div style={pageStyle}>
      <PageHeader title="Stock" subtitle="What is where, what expires, what it is worth" />
      <LoadState loading={loading} error={error} onRetry={reload}>
        {s ? (
          <>
            <Tabs tabs={tabs} active={tab} onChange={setTab} />
            {tab === "stock" ? <ByWarehouse s={s} /> : null}
            {tab === "expiring" ? <Expiring s={s} /> : null}
            {tab === "ages" ? <Ages /> : null}
            {tab === "serial" ? <SerialSearch /> : null}
            {tab === "deposits" ? <Deposits s={s} /> : null}
            {tab === "value" ? <Value /> : null}
          </>
        ) : null}
      </LoadState>
    </div>
  );
}
